<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import { usePriceFormatter2 } from "~/utils/priceFormatter";
import { useMainStore } from "~/stores/main";
import { viewQuickViewItem as sendViewQuickViewItem } from "~/composables/useAnalytics";

const props = defineProps<{
  product: Record<string, any>;
  palette: string | undefined;
  disableVariantChangeOnSwipe?: boolean;
  onlyQuickView?: boolean;
}>();

const { productPageUrl, getAssetUrl, getMasterProductBadge } = useHelpers();

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const themeGroup = config.public.theme.group;
const { masterProductImageAltTag } = useHelpers();

// -----------------------
// reactive properties
// -----------------------
const productImageRef = ref("");
const paletteDataRef = ref();
const currentPaletteRef = ref(); // this stores the currently viewed palette (dot)
const currentColorRef = ref(); // this stores the currently viewed color
let currentPaletteColorIndex = 0; // this stores the currently viewed color of the current dot(palette), so we can cycle over them
const priceFieldToUseRef = ref("calculated_base_prices");
const { status: authStatus, data: authData } = useAuth();
const badge = ref();

// Touch detection
const isTouchDevice = ref(false);

// -----------------------
// vue events
// -----------------------
onMounted(() => {
  calculateRefs();

  // Detect touch device
  isTouchDevice.value =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
});

watch(
  () => props.palette,
  (newValue /*, oldValu */) => {
    currentPaletteColorIndex = 0;
    currentPaletteRef.value = newValue?.toString() ? newValue.toString() : "";
    // console.log(`Setting palette for product "${props.product.name}" changed:`, currentPaletteRef.value);
  },
);

watch(
  () => currentPaletteRef.value,
  () => {
    currentPaletteColorIndex = 0;
    showPaletteImage();
    // console.log(`Applying Palette for product "${props.product.name}" changed:`, currentPaletteRef.value);
  },
);

// -----------------------
// helper methods
// -----------------------
const calculateRefs = () => {
  // calculate the paletteDataRef
  const colors = new Map<string, string[]>();
  for (const product of props.product.products) {
    if (product.product.product_paletta_property && product.product.stock > 0) {
      if (!colors.get(product.product.product_paletta_property))
        colors.set(product.product.product_paletta_property, [
          product.product.product_szin_property,
        ]);
      if (
        !colors
          .get(product.product.product_paletta_property)
          ?.includes(product.product.product_szin_property)
      )
        colors
          .get(product.product.product_paletta_property)
          ?.push(product.product.product_szin_property);
    }
  }
  // console.log('paletteDataRef', colors);
  paletteDataRef.value = colors;

  // default image
  // console.log('props.palette: ', props.palette);
  if (props.palette) {
    currentPaletteRef.value = props.palette;
    showPaletteImage();
  } else {
    showDefaultImage();
  }

  // TV prices
  if (authStatus.value === "authenticated") {
    if (
      Array.isArray(authData?.value?.usergroups) &&
      authData?.value?.usergroups?.includes(1)
    ) {
      priceFieldToUseRef.value = "calculated_pricelist_1_prices";
    }
  }

  badge.value = getMasterProductBadge(props.product, priceFieldToUseRef.value);
};

const showDefaultImage = () => {
  if (Array.isArray(props.product.products)) {
    for (const variant of props.product.products) {
      if (
        variant.product.stock > 0 &&
        Array.isArray(variant.images) &&
        variant.images.length
      ) {
        currentColorRef.value = variant.product.product_szin_property;
        productImageRef.value = getAssetUrl(variant.images[0]);

        break;
      }
    }
  }
};

const paletteDotClasses = (color: string) => {
  const classes: string[] = [
    "rounded-full",
    "mr-1",
    "inline-block",
    "h-[16px]",
    "w-[16px]",
    "palette-dot",
    "text-center",
    "cursor-pointer",
  ];

  classes.push("bg-palette-" + color);

  return classes.join(" ");
};

const paletteColors = () => {
  const colors = new Map<string, string[]>();
  for (const {
    product: {
      product_paletta_property: palette = null,
      product_szin_property: color = null,
      stock = 0,
    },
  } of props.product.products) {
    if (palette && stock > 0) {
      if (!colors.get(palette)) colors.set(palette, [color]);
      if (!colors.get(palette)?.includes(color))
        colors.get(palette)?.push(color);
    }
  }

  return Object.fromEntries(colors);
};

const showNextImageForCurrentPalette = () => {
  // console.log('showNextImageForCurrentPalette');

  const paletteData = paletteDataRef.value.get(currentPaletteRef.value);
  // console.log('paletteData', paletteData);

  if (currentPaletteRef.value && paletteData) {
    let currentImageIndex = 0;
    currentPaletteColorIndex++;
    if (currentPaletteColorIndex >= paletteData.length)
      currentPaletteColorIndex = 0;
    if (currentImageIndex >= paletteData.length) currentImageIndex = 0;
    for (const product of props.product.products) {
      if (
        product.product.product_paletta_property === currentPaletteRef.value &&
        product.images.length
      ) {
        if (currentImageIndex === currentPaletteColorIndex) {
          currentColorRef.value = product.product.product_szin_property;
          productImageRef.value = getAssetUrl(product.images[0]);
          return;
        }
        currentImageIndex++;
      }
    }
  }
};

const setCurrentPalette = (palette: string | undefined) => {
  if (palette && currentPaletteRef.value !== palette) {
    // need to switch to a new palette
    currentPaletteColorIndex = 0;
    currentPaletteRef.value = palette;
  } else {
    // need to show the next color from the current palette
    showNextImageForCurrentPalette();
  }
};

const showPaletteImage = () => {
  if (!currentPaletteRef.value) {
    showDefaultImage();
    return;
  }

  for (const product of props.product.products) {
    if (
      product.product.product_paletta_property === currentPaletteRef.value &&
      product.product.stock > 0 &&
      product.images.length
    ) {
      currentColorRef.value = product.product.product_szin_property;
      productImageRef.value = getAssetUrl(product.images[0]);
      return;
    }
  }
  showDefaultImage();
};

const productPageLink = () => {
  return productPageUrl(props.product) + `?color=${currentColorRef.value}`;
};

const nextVariant = () => {
  const colorsByPalette = paletteColors();
  const palettes = Object.keys(colorsByPalette);
  const currentPalette = Object.keys(colorsByPalette).find((palette) => {
    return colorsByPalette[palette].includes(currentColorRef.value);
  });
  const currentPaletteIndex = palettes.indexOf(currentPalette);
  const nextPaletteIndex =
    currentPaletteIndex === palettes.length - 1 ? 0 : currentPaletteIndex + 1;
  const nextPalette = palettes[nextPaletteIndex];

  setCurrentPalette(nextPalette);
};

const previousVariant = () => {
  const colorsByPalette = paletteColors();
  const palettes = Object.keys(colorsByPalette);
  const currentPalette = Object.keys(colorsByPalette).find((palette) => {
    return colorsByPalette[palette].includes(currentColorRef.value);
  });
  const currentPaletteIndex = palettes.indexOf(currentPalette);
  const previousPaletteIndex =
    currentPaletteIndex === 0 ? palettes.length - 1 : currentPaletteIndex - 1;
  const previousPalette = palettes[previousPaletteIndex];

  setCurrentPalette(previousPalette);
};

// Quick view functions
const mainStore = useMainStore();

const openQuickView = (event: Event) => {
  event.preventDefault();
  // event.stopPropagation();

  // Always update the product, even if panel is already visible
  mainStore.quickViewProduct = props.product;

  // Only set visible if not already visible to avoid closing animation
  if (!mainStore.quickViewVisible) {
    mainStore.quickViewVisible = true;
  }

  sendViewQuickViewItem(mainStore.quickViewProduct);
};
</script>

<template>
  <div
    :id="`product-${product.sku}`"
    v-touch:swipe.left="
      !props.disableVariantChangeOnSwipe ? nextVariant : undefined
    "
    v-touch:swipe.right="
      !props.disableVariantChangeOnSwipe ? previousVariant : undefined
    "
    class="bg-white rounded-16 flex flex-col justify-between shadow-md"
  >
    <!-- Content at the top of the element -->
    <div class="relative">
      <!-- badge -->
      <div
        v-if="badge && badge.type !== 'none'"
        class="absolute top-[10px] left-[0px] z-10"
      >
        <span
          v-if="badge.type === 'discountPercent'"
          class="bg-brand-highlight text-white pl-2 pr-2 rounded-tr-[4px] rounded-br-[4px] font-bold text-sm"
          >-{{ badge.value }} %</span
        >
      </div>

      <div class="p-2 md:p-3 lg:p-5">
        <!-- image -->
        <div
          class="relative rounded overflow-visible flex justify-center items-center group"
        >
          <a
            v-if="onlyQuickView !== true"
            :href="productPageLink()"
            class="w-full h-full flex justify-center"
          >
            <NuxtImg
              v-if="productImageRef"
              width="300"
              class="object-center object-contain h-full aspect-square transition-transform duration-300 ease-in-out group-hover:scale-105"
              :src="productImageRef"
              format="webp"
              loading="lazy"
              :alt="masterProductImageAltTag(product)"
            />
            <div
              v-else
              class="text-center text-surface00 transition-transform duration-300 ease-in-out group-hover:scale-105"
            >
              {{ product.sku }}
            </div>
          </a>
          <div v-else class="w-full h-full flex justify-center">
            <div
              v-if="
                useRuntimeConfig().public.experimentalFeatures.includes(
                  '_quickView_',
                )
              "
              class="cursor-pointer"
            >
              <NuxtImg
                v-if="productImageRef"
                width="300"
                :alt="masterProductImageAltTag(product)"
                class="object-center object-contain h-full aspect-square transition-transform duration-300 ease-in-out group-hover:scale-105"
                :src="productImageRef"
                format="webp"
                loading="lazy"
                @click="openQuickView"
              />
              <div
                v-else
                class="text-center text-surface00 transition-transform duration-300 ease-in-out group-hover:scale-105"
                @click="openQuickView"
              >
                {{ product.sku }}
              </div>
            </div>
          </div>

          <!-- Quick View Button (Desktop Only) -->
          <div
            v-if="
              !isTouchDevice &&
              useRuntimeConfig().public.experimentalFeatures.includes(
                '_quickView_',
              )
            "
            class="absolute inset-0 hidden lg:flex items-end justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
          >
            <i
              class="m-icon-cart text-xl text-white relative bg-brand-headline p-2 hover:shadow-lg transition-shadow rounded-full transition-colors duration-200 pointer-events-auto cursor-pointer"
              @click="openQuickView"
            ></i>
          </div>
        </div>
        <!-- palette dots, name -->
        <div class="mt-4">
          <div class="flex mb-5 justify-center h-[16px]">
            <template
              v-for="(colorArray, color, index) in paletteColors()"
              :key="color.toString()"
            >
              <div
                v-if="index < 4"
                :class="paletteDotClasses(color.toString())"
                @click="setCurrentPalette(color.toString())"
              >
                <!--
                <span
                  v-if="colorArray.length > 1"
                  class="text-center align-top counter text-xs"
                  >{{ colorArray.length }}</span
                >
                -->
              </div>
              <a
                v-if="index == 4 && Object.keys(paletteColors()).length > 5"
                :href="productPageLink()"
                :class="paletteDotClasses('grey')"
                class="bg-[#D9D9D9] relative"
                ><div
                  class="flex items-center justify-center h-[17px] text-black"
                >
                  +
                </div></a
              >
              <div
                v-else-if="index == 4"
                :class="paletteDotClasses(color.toString())"
                @click="setCurrentPalette(color.toString())"
              >
                <span
                  v-if="colorArray.length > 1"
                  class="text-center align-top counter text-xs"
                  >{{ colorArray.length }}</span
                >
              </div>
            </template>
          </div>
          <h2>
            <template v-if="$slots.name">
              <slot name="name" :url="productPageLink()" />
            </template>

            <div v-else class="text-xs md:text-sm">
              <a :href="productPageLink()" class="text-black unstyled">
                <div
                  v-if="themeGroup === 'mountex'"
                  class="font-bold font-lora"
                >
                  {{ product.masterproduct_gyarto_property }}
                </div>
                <div>{{ product.name }}</div>
              </a>
            </div>
          </h2>
        </div>
      </div>
    </div>

    <div class="mt-5 text-xs md:text-sm">
      <ClientOnly>
        <div class="p-2 px-2 md:px-3 lg:px-5">
          <!-- eslint-disable vue/no-v-html -->
          <span
            :title="usePriceFormatter2(product, 'masterProduct').rule"
            v-html="usePriceFormatter2(product, 'masterProduct').line_1"
          />
          <!-- eslint-enable -->
        </div>
        <div
          v-if="usePriceFormatter2(product, 'masterProduct').line_2_highlight"
          class="bg-brand-membership rounded-b-16 p-[8px] px-2 md:px-3 lg:px-5"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="usePriceFormatter2(product, 'masterProduct').line_2" />
          <!-- eslint-enable -->
        </div>
        <div v-else class="p-[8px] px-2 md:px-3 lg:px-5">
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="usePriceFormatter2(product, 'masterProduct').line_2" />
          <!-- eslint-enable -->
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<style scoped></style>
